import { z } from 'zod'

// =============================================================================
// Types
// =============================================================================

export interface PersistedTimerState {
  stopwatch: {
    status: 'idle' | 'running' | 'paused'
    elapsedMs: number
    lastTickAt: number | null
  }
  timer: {
    status: 'idle' | 'running' | 'paused' | 'overage'
    durationMs: number
    remainingMs: number
    lastTickAt: number | null
  }
  activeView: 'stopwatch' | 'timer'
  hasTimer: boolean
  timeWindows?: Array<{
    startedAt: string
    pausedAt: string
    durationMs: number
  }>
  currentWindowStart?: string | null
}

// Runtime schema for validating data read back from localStorage. The stored
// blob crosses a trust boundary — a stale tab from an older deploy, or a future
// shape change, can leave a value that doesn't match PersistedTimerState — so it
// must be validated, not blindly cast, before `reconstructTime` dereferences
// `stopwatch`/`timer`. Kept in sync with the interface above.
const persistedTimerStateSchema = z.object({
  stopwatch: z.object({
    status: z.enum(['idle', 'running', 'paused']),
    elapsedMs: z.number(),
    lastTickAt: z.number().nullable(),
  }),
  timer: z.object({
    status: z.enum(['idle', 'running', 'paused', 'overage']),
    durationMs: z.number(),
    remainingMs: z.number(),
    lastTickAt: z.number().nullable(),
  }),
  activeView: z.enum(['stopwatch', 'timer']),
  hasTimer: z.boolean(),
  timeWindows: z
    .array(
      z.object({
        startedAt: z.string(),
        pausedAt: z.string(),
        durationMs: z.number(),
      }),
    )
    .optional(),
  currentWindowStart: z.string().nullable().optional(),
})

// =============================================================================
// Defaults
// =============================================================================

export const DEFAULT_TIMER_STATE: PersistedTimerState = {
  stopwatch: { status: 'idle', elapsedMs: 0, lastTickAt: null },
  timer: { status: 'idle', durationMs: 0, remainingMs: 0, lastTickAt: null },
  activeView: 'stopwatch',
  hasTimer: false,
}

// =============================================================================
// Storage Keys
// =============================================================================

function stateKey(slug: string): string {
  return `bctci-timer:${slug}`
}

// =============================================================================
// Timer State (per-problem)
// =============================================================================

export function loadTimerState(slug: string): PersistedTimerState {
  try {
    const raw = localStorage.getItem(stateKey(slug))
    if (!raw) return DEFAULT_TIMER_STATE
    // Validate the stored shape before returning — the try/catch only guards
    // JSON syntax errors, not a value whose shape has drifted, which would make
    // `reconstructTime` throw on `stopwatch`/`timer` access. Fall back to the
    // default on any mismatch, same as the parse-failure case.
    const parsed = persistedTimerStateSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : DEFAULT_TIMER_STATE
  } catch {
    return DEFAULT_TIMER_STATE
  }
}

export function saveTimerState(slug: string, state: PersistedTimerState): void {
  localStorage.setItem(stateKey(slug), JSON.stringify(state))
}

export function clearTimerState(slug: string): void {
  localStorage.removeItem(stateKey(slug))
}

// =============================================================================
// Time Reconstruction
// =============================================================================

/**
 * Reconstruct timer state from localStorage after a page reload.
 *
 * Key invariant: time between browser close and reopen is NOT active work time.
 * - If beforeunload fired correctly, the state is already paused with closed windows.
 * - If the browser crashed (beforeunload didn't fire), we close the open window
 *   at lastTickAt (last known active moment) rather than adding the gap.
 */
export function reconstructTime(
  state: PersistedTimerState,
): PersistedTimerState {
  const result = structuredClone(state)

  // Close any open time window at the last known tick time (not current time).
  // This handles browser crashes where beforeunload didn't fire.
  if (result.currentWindowStart) {
    const lastTick = result.stopwatch.lastTickAt
    if (lastTick) {
      const pausedAt = new Date(lastTick).toISOString()
      const durationMs =
        lastTick - new Date(result.currentWindowStart).getTime()
      if (!result.timeWindows) result.timeWindows = []
      result.timeWindows.push({
        startedAt: result.currentWindowStart,
        pausedAt,
        durationMs: Math.max(0, durationMs),
      })
    }
    // Discard open window (either closed above or no lastTickAt to close with)
    result.currentWindowStart = null
  }

  // Force pause — can't restore a running state from localStorage.
  // Do NOT add gap to elapsedMs — inactive time is not work time.
  if (result.stopwatch.status === 'running') {
    result.stopwatch.status = 'paused'
  }
  result.stopwatch.lastTickAt = null

  if (result.timer.status === 'running' || result.timer.status === 'overage') {
    result.timer.status = 'paused'
  }
  result.timer.lastTickAt = null

  return result
}
